import { useEffect, useMemo, useState } from "react";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Copy, Check, ArrowRight, Ticket } from "lucide-react";
import { BRAND } from "@/lib/brand";
import { SITE_URL } from "@/lib/siteConfig";
import { openRequestAccess } from "@/components/landing/RequestAccessDialog";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

const TITLE = "Waitlist | Myelina Health";
const DESC =
  "Join the Myelina waitlist. Invite-only pilot for women living with MS. Share your referral link to move up.";

const Waitlist = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const ref = params.get("ref") || "";
  const inviteFromUrl = (params.get("code") || "").trim().toUpperCase();
  const [copied, setCopied] = useState(false);
  const [email, setEmail] = useState("");

  // Deterministic short handle for the visitor, used to build a shareable link.
  // We don't create a real referral until the user requests access; this is a
  // pre-commitment nudge so the link *feels* real from the first visit.
  const myLink = useMemo(() => {
    let handle = "";
    try {
      handle = localStorage.getItem("myelina.waitlist.handle") || "";
      if (!handle) {
        handle = Math.random().toString(36).slice(2, 8);
        localStorage.setItem("myelina.waitlist.handle", handle);
      }
    } catch {
      handle = "friend";
    }
    return `${SITE_URL}/waitlist?ref=${handle}`;
  }, []);

  useEffect(() => {
    document.title = TITLE;
    const setMeta = (name: string, content: string, attr: "name" | "property" = "name") => {
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    setMeta("description", DESC);
    setMeta("og:title", TITLE, "property");
    setMeta("og:description", DESC, "property");
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", `${SITE_URL}/waitlist`);
    if (ref) {
      try {
        sessionStorage.setItem("myelina.ref", ref);
      } catch {
        /* ignore */
      }
    }
  }, [ref]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(myLink);
      setCopied(true);
      toast.success("Referral link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy, long-press the link instead");
    }
  };

  const handleJoin = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedEmail = email.trim();
    // Remember the email so /signup can prefill it, a seamless hand-off from
    // waitlist to account setup to first check-in.
    try {
      if (trimmedEmail) sessionStorage.setItem("myelina.waitlist.email", trimmedEmail);
    } catch {
      /* ignore */
    }
    // If the visitor already has an invite code (via ?code=... or a prior visit),
    // skip the request-access dialog and send them straight into signup.
    if (inviteFromUrl) {
      const qs = new URLSearchParams({ code: inviteFromUrl });
      if (trimmedEmail) qs.set("email", trimmedEmail);
      if (ref) qs.set("ref", ref);
      navigate(`/signup?${qs.toString()}`);
      return;
    }
    openRequestAccess("waitlist", { email: trimmedEmail });
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="container mx-auto px-6 sm:px-10 lg:px-16 py-20 max-w-3xl">
        <p className="text-[11px] tracking-[0.28em] uppercase text-muted-foreground mb-4">
          Invite-only pilot
        </p>
        <h1 className="font-display text-5xl md:text-6xl tracking-tight mb-6">
          Save your seat.
        </h1>
        <p className="lead-italic text-foreground/80 mb-10 max-w-2xl">
          Myelina is opening in small, careful waves. Add your email to hold a
          spot, and if you'd like to move up, share your link with someone who
          would benefit.
        </p>

        {ref && (
          <div className="mb-8 rounded-lg border border-primary/20 bg-primary/[0.04] p-4 text-sm text-foreground/80">
            You were invited by <span className="font-medium">a friend</span>.
            That counts, you'll be prioritized on the next wave.
          </div>
        )}

        <form
          onSubmit={handleJoin}
          className="flex flex-col sm:flex-row gap-3 mb-14"
          aria-label="Join the Myelina waitlist"
        >
          <Input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="h-12 flex-1"
            aria-label="Your email"
          />
          <Button type="submit" size="lg" variant="premium" className="min-h-12">
            Join the waitlist
            <ArrowRight className="w-4 h-4 ml-1" aria-hidden="true" />
          </Button>
        </form>

        <div className="-mt-10 mb-14 text-sm text-foreground/70 flex items-center gap-2">
          <Ticket className="w-4 h-4 text-primary" aria-hidden="true" />
          <span>
            Already have an invite code?{" "}
            <Link
              to={`/signup${email.trim() ? `?email=${encodeURIComponent(email.trim())}` : ""}`}
              className="underline underline-offset-2 font-medium text-foreground hover:text-primary"
            >
              Continue to setup →
            </Link>
          </span>
        </div>

        <section className="mb-16">
          <h2 className="font-display text-2xl mb-4 text-foreground">
            Skip the line
          </h2>
          <p className="text-foreground/80 mb-4 leading-relaxed">
            Each friend who joins with your link moves you up one wave. It's
            the same product either way, this just helps us reach the people
            who need it, faster.
          </p>
          <div className="flex flex-col sm:flex-row gap-2 items-stretch">
            <Input
              readOnly
              value={myLink}
              className="h-12 flex-1 font-mono text-sm"
              aria-label="Your personal referral link"
              onFocus={(e) => e.currentTarget.select()}
            />
            <Button
              type="button"
              onClick={copyLink}
              variant="outline"
              size="lg"
              className="min-h-12"
              aria-label="Copy referral link"
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4 mr-1" aria-hidden="true" /> Copied
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 mr-1" aria-hidden="true" /> Copy link
                </>
              )}
            </Button>
          </div>
        </section>

        <section className="space-y-8 text-foreground/85 leading-[1.8]">
          <div>
            <h2 className="font-display text-2xl mb-3 text-foreground">
              What you get on day one
            </h2>
            <ul className="space-y-2 text-[16px]">
              <li>• A ~60-second daily check-in built for fatigue days.</li>
              <li>• Your first energy pattern once you have ~60 days of data.</li>
              <li>• Trends with 95% confidence bands, no false certainty.</li>
              <li>• A quiet companion. No streaks that punish you.</li>
            </ul>
          </div>

          <div>
            <h2 className="font-display text-2xl mb-3 text-foreground">
              What we're careful about
            </h2>
            <ul className="space-y-2 text-[16px]">
              <li>• Myelina is a wellness tool. It's not a medical device.</li>
              <li>• We never sell your data. Ever. See <a href="/trust" className="underline">Trust</a>.</li>
              <li>• You can delete everything, any time, from Settings.</li>
            </ul>
          </div>
        </section>

        <p className="mt-16 text-xs text-muted-foreground">
          {BRAND.legalName}. Maintained by the founder, reach us at{" "}
          <a href="mailto:hello@myelinahealth.com" className="underline">
            hello@myelinahealth.com
          </a>
          .
        </p>
      </main>
      <Footer />
    </div>
  );
};

export default Waitlist;
